/**
 * TeamJudgmentPattern Management Page
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  getPatterns,
  getPatternById,
  createPattern,
  updatePattern,
  deletePattern,
  exportPattern,
  importPattern,
  bulkImportPatterns,
  TeamJudgmentPattern,
  GetPatternsParams,
  CreatePatternInput,
  BulkImportMode,
  BulkImportResult,
} from '../api/teamJudgmentPatternApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TeamJudgmentPatternManagementPage() {
  const [patterns, setPatterns] = useState<TeamJudgmentPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [nameFilter, setNameFilter] = useState('');

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<TeamJudgmentPattern | null>(null);

  const [formData, setFormData] = useState<Partial<CreatePatternInput>>({ scoreConditions: {} });
  const [tabValue, setTabValue] = useState(0);

  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportMode, setBulkImportMode] = useState<BulkImportMode>('add');
  const [patternsToImport, setPatternsToImport] = useState<CreatePatternInput[]>([]);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResult | null>(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);

  const loadPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetPatternsParams = {
        name: nameFilter || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      const response = await getPatterns(params);
      setPatterns(response.patterns);
      setTotalCount(response.pagination.total);
    } catch (err) {
      setError('Failed to load patterns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatterns();
  }, [page, rowsPerPage, nameFilter]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = async (id: string) => {
    try {
      const pattern = await getPatternById(id);
      setSelectedPattern(pattern);
      setViewDialogOpen(true);
    } catch (err) {
      setError('Failed to load pattern details');
    }
  };

  const handleCreate = () => {
    setFormData({ name: '', description: '', scoreConditions: {} });
    setTabValue(0);
    setCreateDialogOpen(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.name || !formData.description) {
      setError('Name and description are required');
      return;
    }
    try {
      await createPattern(formData as CreatePatternInput);
      setCreateDialogOpen(false);
      setFormData({ scoreConditions: {} });
      loadPatterns();
    } catch (err) {
      setError('Failed to create pattern');
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const pattern = await getPatternById(id);
      setSelectedPattern(pattern);
      const formDataConverted = {
        ...pattern,
        tdesc11: pattern.tdesc11 ?? undefined,
        tdesc12: pattern.tdesc12 ?? undefined,
        tdesc13: pattern.tdesc13 ?? undefined,
        tdesc14: pattern.tdesc14 ?? undefined,
        tdesc15: pattern.tdesc15 ?? undefined,
        tdesc16: pattern.tdesc16 ?? undefined,
        tdesc17: pattern.tdesc17 ?? undefined,
        tdesc18: pattern.tdesc18 ?? undefined,
        tdesc19: pattern.tdesc19 ?? undefined,
        tdesc21: pattern.tdesc21 ?? undefined,
        tdesc22: pattern.tdesc22 ?? undefined,
        tdesc23: pattern.tdesc23 ?? undefined,
        tdesc24: pattern.tdesc24 ?? undefined,
        tdesc25: pattern.tdesc25 ?? undefined,
        tdesc26: pattern.tdesc26 ?? undefined,
        tdesc27: pattern.tdesc27 ?? undefined,
      };
      setFormData(formDataConverted);
      setTabValue(0);
      setEditDialogOpen(true);
    } catch (err) {
      setError('Failed to load pattern details');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedPattern) return;
    try {
      await updatePattern(selectedPattern.id, formData);
      setEditDialogOpen(false);
      setSelectedPattern(null);
      setFormData({ scoreConditions: {} });
      loadPatterns();
    } catch (err) {
      setError('Failed to update pattern');
    }
  };

  const handleDeleteClick = (pattern: TeamJudgmentPattern) => {
    setSelectedPattern(pattern);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPattern) return;
    try {
      await deletePattern(selectedPattern.id);
      setDeleteDialogOpen(false);
      setSelectedPattern(null);
      loadPatterns();
    } catch (err) {
      setError('Failed to delete pattern');
    }
  };

  const handleExport = async (id: string) => {
    try {
      const jsonData = await exportPattern(id);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-judgment-pattern-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export pattern');
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        await importPattern(text);
        loadPatterns();
      } catch (err) {
        setError('Failed to import pattern');
      }
    };
    input.click();
  };

  const handleBulkImportFromFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const patterns = JSON.parse(text);
        if (!Array.isArray(patterns)) {
          setError('JSON file must contain an array of patterns');
          return;
        }
        setPatternsToImport(patterns);
        setBulkImportResult(null);
        setBulkImportDialogOpen(true);
      } catch (err) {
        setError('Failed to read JSON file');
      }
    };
    input.click();
  };

  const handleExecuteBulkImport = async () => {
    if (patternsToImport.length === 0) return;
    setBulkImportLoading(true);
    setBulkImportResult(null);
    setError(null);
    try {
      const result = await bulkImportPatterns(patternsToImport, bulkImportMode);
      setBulkImportResult(result);
      if (result.successCount > 0) loadPatterns();
    } catch (err: unknown) {
      // 詳細なエラーメッセージを表示
      const axiosError = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError?.response?.data?.error || axiosError?.message || 'Unknown error';
      setError(`Bulk import failed: ${errorMessage}`);
      console.error('Bulk import error:', err);
    } finally {
      setBulkImportLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">チーム判定パターン管理</Typography>
        <Box>
          <Button startIcon={<UploadIcon />} onClick={handleBulkImportFromFile} sx={{ mr: 1 }}>
            Bulk Import
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            Create Pattern
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Pattern Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
          />
          <Button variant="outlined" onClick={() => setNameFilter('')}>Clear</Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center">Loading...</TableCell></TableRow>
            ) : patterns.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No patterns found</TableCell></TableRow>
            ) : (
              patterns.map((pattern) => (
                <TableRow key={pattern.id}>
                  <TableCell>{pattern.name}</TableCell>
                  <TableCell>{pattern.description.substring(0, 50)}...</TableCell>
                  <TableCell>{new Date(pattern.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(pattern.id)}><ViewIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(pattern.id)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleExport(pattern.id)}><DownloadIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(pattern)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pattern Details</DialogTitle>
        <DialogContent>
          {selectedPattern && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="ID" value={selectedPattern.id} InputProps={{ readOnly: true }} />
              <TextField label="Name" value={selectedPattern.name} InputProps={{ readOnly: true }} />
              <TextField label="Description" value={selectedPattern.description} multiline rows={3} InputProps={{ readOnly: true }} />
              <TextField label="Score Conditions (JSON)" value={JSON.stringify(selectedPattern.scoreConditions, null, 2)} multiline rows={5} InputProps={{ readOnly: true }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setViewDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{createDialogOpen ? 'Create Pattern' : 'Edit Pattern'}</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Basic Info" />
            <Tab label="Team Desc (tdesc1x)" />
            <Tab label="Team Desc (tdesc2x)" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField label="Name *" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <TextField label="Description *" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} />
              <TextField
                label="Score Conditions (JSON) *"
                value={JSON.stringify(formData.scoreConditions || {}, null, 2)}
                onChange={(e) => { try { setFormData({ ...formData, scoreConditions: JSON.parse(e.target.value) }); } catch {} }}
                multiline
                rows={5}
                helperText='Example: {"TP": "RC"} or {"A": {"min": 10, "max": 20}}'
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['tdesc11', 'tdesc12', 'tdesc13', 'tdesc14', 'tdesc15', 'tdesc16', 'tdesc17', 'tdesc18', 'tdesc19'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={2}
                />
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['tdesc21', 'tdesc22', 'tdesc23', 'tdesc24', 'tdesc25', 'tdesc26', 'tdesc27'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={2}
                />
              ))}
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }}>Cancel</Button>
          <Button onClick={createDialogOpen ? handleSaveCreate : handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete pattern "{selectedPattern?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportDialogOpen} onClose={() => !bulkImportLoading && setBulkImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>バルクインポート</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">{patternsToImport.length} 件のパターンをインポートします。</Alert>
            <FormControl component="fieldset">
              <FormLabel component="legend">インポートモード</FormLabel>
              <RadioGroup value={bulkImportMode} onChange={(e) => setBulkImportMode(e.target.value as BulkImportMode)}>
                <FormControlLabel value="add" control={<Radio />} label="追加 - 既存のパターンを保持し、新しいパターンを追加" />
                <FormControlLabel value="replace" control={<Radio />} label="総入れ替え - 既存のパターンをすべて削除し、新しいパターンで置き換え" />
              </RadioGroup>
            </FormControl>
            {bulkImportMode === 'replace' && (
              <Alert severity="warning">警告: 総入れ替えモードでは、既存のすべてのパターンが削除されます。</Alert>
            )}
            {bulkImportLoading && <LinearProgress />}
            {bulkImportResult && (
              <Box>
                <Chip label={`成功: ${bulkImportResult.successCount}`} color="success" sx={{ mr: 1 }} />
                <Chip label={`エラー: ${bulkImportResult.errorCount}`} color={bulkImportResult.errorCount > 0 ? 'error' : 'default'} />
                {bulkImportResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {bulkImportResult.errors.slice(0, 5).map((err, i) => (
                      <div key={i}>{err.name}: {err.error}</div>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkImportDialogOpen(false)} disabled={bulkImportLoading}>閉じる</Button>
          <Button onClick={handleExecuteBulkImport} variant="contained" disabled={bulkImportLoading || patternsToImport.length === 0}>
            インポート実行
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
